import { FFT_SIZE } from '@rapoo/device-sdk'

/** 音频采集状态（web 层音频采集专属，非灯效算法）。 */
export type AudioCaptureStatus = 'idle' | 'requesting' | 'active' | 'error'

export const AudioCaptureErrorCode = {
  NoAudioTrack: 'no_audio_track',
} as const

export type AudioCaptureErrorCode =
  (typeof AudioCaptureErrorCode)[keyof typeof AudioCaptureErrorCode]

export class AudioCaptureError extends Error {
  readonly code: AudioCaptureErrorCode

  constructor(code: AudioCaptureErrorCode, message: string) {
    super(message)
    this.name = 'AudioCaptureError'
    this.code = code
  }
}

export type AudioCaptureListener = (status: AudioCaptureStatus, message?: string) => void

/**
 * 浏览器系统音频采集：
 * - 优先 getDisplayMedia（分享标签页/窗口并勾选「分享音频」）
 * - 失败时回退 getUserMedia 麦克风，便于无系统环回时调试算法
 */
export class SystemAudioCapture {
  private audioCtx: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private stream: MediaStream | null = null
  private freqBuffer: Uint8Array | null = null
  private status: AudioCaptureStatus = 'idle'
  private message = ''
  private usingMicFallback = false
  private readonly listeners = new Set<AudioCaptureListener>()

  onStatus(listener: AudioCaptureListener): () => void {
    this.listeners.add(listener)
    listener(this.status, this.message)
    return () => this.listeners.delete(listener)
  }

  getStatus(): AudioCaptureStatus {
    return this.status
  }

  getMessage(): string {
    return this.message
  }

  isUsingMicFallback(): boolean {
    return this.usingMicFallback
  }

  getAnalyser(): AnalyserNode | null {
    return this.analyser
  }

  getSampleRate(): number {
    return this.audioCtx?.sampleRate ?? 48000
  }

  getFrequencyData(out?: Uint8Array): Uint8Array | null {
    if (!this.analyser || !this.freqBuffer) return null
    const target = out && out.length === this.freqBuffer.length ? out : this.freqBuffer
    // 兼容部分 TS DOM 对 getByteFrequencyData 参数类型的收紧
    this.analyser.getByteFrequencyData(target as never)
    return target
  }

  async startSystemAudio(): Promise<void> {
    this.setStatus('requesting', '请选择要分享的标签页/窗口，并勾选「分享音频」')
    this.usingMicFallback = false
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        } as MediaTrackConstraints,
      })
      // 只要音频轨；视频轨立即停用以节省资源
      stream.getVideoTracks().forEach((t) => {
        t.stop()
        stream.removeTrack(t)
      })
      if (stream.getAudioTracks().length === 0) {
        stream.getTracks().forEach((t) => t.stop())
        throw new AudioCaptureError(
          AudioCaptureErrorCode.NoAudioTrack,
          '未获取到音频轨道，请勾选「分享系统音频/标签页音频」',
        )
      }
      await this.bindStream(stream)
      this.setStatus('active', '系统音频采集中')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      this.setStatus('error', msg || '系统音频采集失败')
      throw err
    }
  }

  async startMicrophone(): Promise<void> {
    this.setStatus('requesting', '正在请求麦克风权限…')
    this.usingMicFallback = true
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
        video: false,
      })
      await this.bindStream(stream)
      this.setStatus('active', '麦克风采集中（调试回退）')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      this.setStatus('error', msg || '麦克风采集失败')
      throw err
    }
  }

  stop(): void {
    this.teardown()
    this.setStatus('idle', '')
  }

  private async bindStream(stream: MediaStream): Promise<void> {
    this.teardown()
    this.stream = stream
    stream.getAudioTracks().forEach((track) => {
      track.addEventListener('ended', () => {
        this.teardown()
        this.setStatus('error', '音频分享已结束')
      })
    })

    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    this.audioCtx = new Ctx()
    if (this.audioCtx.state === 'suspended') {
      await this.audioCtx.resume()
    }
    this.analyser = this.audioCtx.createAnalyser()
    this.analyser.fftSize = FFT_SIZE
    this.analyser.smoothingTimeConstant = 0.55
    this.analyser.minDecibels = -90
    this.analyser.maxDecibels = -20
    this.freqBuffer = new Uint8Array(this.analyser.frequencyBinCount)
    this.source = this.audioCtx.createMediaStreamSource(stream)
    this.source.connect(this.analyser)
  }

  private teardown(): void {
    this.source?.disconnect()
    this.source = null
    this.analyser = null
    this.freqBuffer = null
    if (this.audioCtx) {
      void this.audioCtx.close().catch(() => undefined)
      this.audioCtx = null
    }
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
    this.usingMicFallback = false
  }

  private setStatus(status: AudioCaptureStatus, message: string): void {
    this.status = status
    this.message = message
    for (const listener of this.listeners) listener(status, message)
  }
}
